/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { VueConstructor } from 'vue';
import sensors from 'sa-sdk-javascript';
import Exposure from 'sa-sdk-javascript/dist/web/plugin/exposure/index.es6';
import rgp from 'sa-sdk-javascript/dist/web/plugin/register-properties/index.es6';

// 这里用裸 axios 而不是 @yixin/axios 是为了避免循环依赖
// （封装过的axios网络请求会上报dev事件，如果dev事件上报的describe超长就会死循环）
import axios from 'axios';

const MAX_STRING_LENGTH = 8192;

export interface TrackingProps {
  $title?: string;
  $referrer?: string;
  $url?: string;
  $url_path?: string;
  utm_source?: string | null;
  desc?: string | null;
  describe?: string | null;
  abtest?: string | null;
  feature_flags?: string | null;
  $lib_version?: string | null;
}

function defaultProps(): TrackingProps {
  return {
    $title: document.title,
    $url: window.location.href,
    $url_path: window.location.pathname,
    $referrer: window.__vue_router_referrer__,
  };
}

/**
 * 检查describe字段长度是否超过限制
 * @param name
 * @param properties
 */
function checkDescribeLengthLimit(event: string, properties?: Record<string, any>) {
  if (properties?.describe && properties.describe.length > MAX_STRING_LENGTH) {
    axios
      .post(
        '/app/client/message/report',
        {
          msg: JSON.stringify({
            tag: '神策describe字段长度超过限制',
            platform: 'web',
            event_name: event,
            url: window.location.href,
            desc: properties?.desc,
            describe_length: properties.describe.length,
            limit_length: MAX_STRING_LENGTH,
            sdk_version: properties.$lib_version,
            timestamp: Date.now(),
          }),
          type: 'Error',
        },
        {
          timeout: 3000,
        },
      )
      .catch(() => {});
  }
}

/**
 * 创建自定义埋点方法
 * @param name 事件名
 * @param properties 传递的属性
 * @returns Promise<void>
 */
function createTracker<T extends TrackingProps>(name: string, properties: T): Promise<void> {
  return new Promise((resolve) => {
    sensors.track(
      name,
      {
        ...defaultProps(),
        ...properties,
      },
      () => {
        resolve();
      },
    );
  });
}

let initialized = false;

export interface SATrackingOptions {
  projectName: string;
  showLog?: boolean;
}

/**
 * $WebPageLeave 事件的自定义属性
 */
export interface PageLeaveProps {
  desc?: string;
  describe?: string;
}
const pageLeaveProps: Required<PageLeaveProps> = {
  desc: '',
  describe: '',
};

/**
 * 设置当前页面 $WebPageLeave 事件的自定义属性
 * 属性会在事件上报后自动清空，不会带到下一个页面，无需手动重置
 */
function setupPageLeaveProps(props: PageLeaveProps) {
  if (props.desc) {
    pageLeaveProps.desc = props.desc;
  }

  if (props.describe) {
    pageLeaveProps.describe = props.describe;
  }
}

/**
 * 仅在 $WebPageLeave 上报后由下方 hookRegister 调用，未对外导出
 */
function clearPageLeaveProps() {
  pageLeaveProps.desc = '';
  pageLeaveProps.describe = '';
}

let exposure: any;
let registerPlugin: any;

/**
 * 初始化神策SDK
 */
function init(options: SATrackingOptions) {
  if (initialized) return;

  // 仅指定页面上报 $WebPageLeave 事件
  sensors.use('PageLeave', {
    custom_props: {
      desc() {
        if (pageLeaveProps.desc) {
          return pageLeaveProps.desc;
        }
      },
      describe() {
        if (pageLeaveProps.describe) {
          return pageLeaveProps.describe;
        }
      },
    },
    isCollectUrl(url: string) {
      return url.includes('/misc/lending-institution');
    },
  });
  exposure = sensors.use(Exposure, {
    area_rate: 0.5,
    stay_duration: 0,
    repeated: true,
  });

  registerPlugin = sensors.use(rgp);

  registerPlugin.hookRegister((info: any) => {
    // 上报后清空，避免属性残留到下一个页面
    // 此时清空是安全的：custom_props 的函数在更早的 basicProps 阶段就已求值，
    // hookRegister 挂在之后的 extendProps 阶段，拿到的 properties 里已是快照
    if (info.event === '$WebPageLeave') {
      clearPageLeaveProps();
    }

    checkDescribeLengthLimit(info.event, info.properties);
  });

  sensors.init({
    server_url: 'https://sadata.yixin.com/sa?project=' + options.projectName,
    show_log: options.showLog ?? false,
    callback_timeout: 500,
    app_js_bridge: true,
    max_string_length: MAX_STRING_LENGTH,
    send_type: 'ajax',
    heatmap: {
      clickmap: 'default',
      scroll_notice_map: 'default',
      collect_element: function (element_target: HTMLElement) {
        if (
          element_target.getAttribute('disabled') != null ||
          element_target.getAttribute('data-sa-disabled') != null
        ) {
          return false;
        }

        return true;
      },
      custom_property: function (element_target: HTMLElement) {
        const properties: { desc?: string; describe?: string } = {};

        const desc = element_target.getAttribute('data-sa-desc');
        if (desc != null) {
          properties.desc = desc;
        }

        const describe = element_target.getAttribute('data-sa-describe');
        if (describe != null) {
          properties.describe = describe;
        }

        const placeholder = element_target.getAttribute('placeholder');
        if (
          element_target.tagName.toLowerCase() === 'input' &&
          placeholder != null &&
          (properties.desc == null || properties.desc === '')
        ) {
          properties.desc = placeholder;
        }

        return properties;
      },
    },
  });

  initialized = true;
}

/**
 * 神策登录用户ID
 */
function login(id?: string | number): Promise<void> {
  return new Promise((resolve) => {
    if (!initialized) {
      resolve();
      return;
    }

    sensors.login(String(id), () => {
      resolve();
    });
  });
}

/**
 * 神策退出登录
 */
function logout() {
  if (!initialized) return;
  sensors.logout();
}

/**
 * 注册全局参数
 */
function registerPage(properties: Record<string, any>) {
  if (!initialized) return;
  // registerPage的属性会执行合并操作，不会覆盖已设置的属性
  sensors.registerPage(properties);
}

/**
 * 清除全局参数
 */
function clearPageRegister() {
  if (!initialized) return;
  // @ts-ignore
  sensors.clearPageRegister(true);
}

/**
 * 弹窗事件埋点
 */
function trackPopup(
  properties: {
    popup_name?: string;
    popup_desc?: string;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('popup', properties);
}

/**
 * PV事件埋点
 */
function trackPageView(properties?: TrackingProps & { is_finished?: 1 | 0 }) {
  if (!initialized) return;
  sensors.quick('autoTrackSinglePage', properties);
}

/**
 * 子页面事件埋点
 */
function trackSubPageView(
  properties: {
    status_name?: string;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('child_page', properties);
}

/**
 * 接口结果埋点
 */
function trackInterfaceResults(properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('interface_results', properties);
}

/**
 * 登录事件埋点
 */
function trackLogin(
  properties: {
    is_new?: boolean | null;
    is_login?: boolean;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('registe', {
    ...properties,
    is_login: true, // 固定传值，数据团队要求的
  });
}

/**
 * 输入内容变更埋点
 */
function trackInputChange(
  properties: {
    custom_element_type: string;
    custom_element_name: string;
    custom_element_class_name: string | null;
    custom_element_selector?: string | null;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('change', properties);
}

/**
 * 点击埋点
 * 仅采集除 a, input, button,textarea 之外的标签
 */
function trackClick(target: HTMLElement, properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return new Promise((resolve) => {
    sensors.quick('trackHeatMap', target, properties, () => {
      resolve();
    });
  });
}

/**
 * 点击埋点
 * 采集所有标签
 */
function trackAllClick(target: HTMLElement, properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return new Promise((resolve) => {
    sensors.quick('trackAllHeatMap', target, properties, () => {
      resolve();
    });
  });
}

/**
 * 滑动区域埋点
 */
function trackSwipe(properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('swipe', properties);
}

/**
 * 根据DOM节点获取选择器路径
 */
function getSelector(el: Element): string {
  // @ts-ignore
  return sensors.heatmap.getDomSelector(el);
}

/**
 * 序列化JSON数据
 */
function serialize(data: any) {
  try {
    return JSON.stringify(data);
  } catch {
    return '';
  }
}

/**
 * 保险单独的PV埋点
 */
function trackBXPageView(properties: TrackingProps) {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('pageview_bx', properties);
}

/**
 * 保险单独的点击埋点
 */
function trackBXClick(properties: TrackingProps) {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('webclick_bx', properties);
}

/**
 * App点击事件
 */
function trackAppClick(properties: TrackingProps) {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('click', properties);
}

/**
 * 调试埋点
 */
function trackDev(properties: TrackingProps) {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('dev', properties);
}

interface ExposureViewOptions {
  eventName: string;
  config?: {
    area_rate?: number;
    stay_duration?: number;
    repeated?: boolean;
  };
  properties?: TrackingProps;
  listener?: {
    shouldExpose: (element: HTMLElement, properties: TrackingProps) => boolean;
    didExpose: (element: HTMLElement, properties: TrackingProps) => void;
  };
}

/**
 * 注册曝光元素
 */
function addExposureView(element: HTMLElement, options: ExposureViewOptions) {
  if (!initialized || !exposure) return;
  exposure.addExposureView(element, options);
}

/**
 * 注销曝光元素
 */
function removeExposureView(element: HTMLElement) {
  if (!initialized || !exposure) return;
  exposure.removeExposureView(element);
}

const SATracking = {
  init,
  login,
  logout,
  registerPage,
  clearPageRegister,
  trackPopup,
  trackClick,
  trackAllClick,
  trackPageView,
  trackSubPageView,
  trackInterfaceResults,
  trackLogin,
  trackInputChange,
  trackSwipe,
  getSelector,
  serialize,
  trackBXPageView,
  trackBXClick,
  trackAppClick,
  trackDev,
  addExposureView,
  removeExposureView,
  setupPageLeaveProps,
};

export function SATrackingPlugin(Vue: VueConstructor) {
  Vue.prototype.$tracking = SATracking;
}

export default SATracking;
